import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Check, X, ArrowRight } from "lucide-react";
import { SunriseCircle } from "../components/icons/SunriseCircle";
import { FadeUp } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 6;
const INTERVAL = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [pkgName, setPkgName] = useState("");

  const poll = useCallback(async (attempt = 0) => {
    if (attempt >= MAX_ATTEMPTS) {
      setState("timeout");
      return;
    }
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      setPkgName(res.data.package_name || "");
      if (res.data.payment_status === "paid") {
        setState("paid");
        return;
      }
      if (res.data.status === "expired") {
        setState("failed");
        return;
      }
      setTimeout(() => poll(attempt + 1), INTERVAL);
    } catch (e) {
      console.error(e);
      setTimeout(() => poll(attempt + 1), INTERVAL);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    poll(0);
  }, [sessionId, poll]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-brand-forest text-brand-cream flex items-center justify-center px-6 grain relative">
      <div className="relative z-10 max-w-lg text-center">
        {state === "checking" && (
          <FadeUp>
            <Loader2 size={48} className="text-brand-goldBright mx-auto animate-spin" />
            <h1 className="font-serif text-4xl md:text-5xl mt-8">Confirming your investment…</h1>
            <p className="text-brand-cream/70 mt-4">One graceful moment while we verify your payment.</p>
          </FadeUp>
        )}

        {state === "paid" && (
          <FadeUp>
            <div className="w-20 h-20 rounded-full bg-brand-cream mx-auto flex items-center justify-center">
              <Check size={40} className="text-brand-forest" />
            </div>
            <SunriseCircle size={28} className="text-brand-goldBright mx-auto mt-8" strokeWidth={1.3} />
            <h1 className="font-serif text-4xl md:text-6xl mt-6 leading-tight">Welcome to your becoming.</h1>
            <p className="text-brand-cream/80 mt-6 text-lg">
              Your place in <span className="text-brand-goldBright italic">{pkgName || "the experience"}</span> is reserved.
              Noelle will reach out personally to schedule your first session.
            </p>
            <Link
              to="/"
              data-testid="success-home-link"
              className="group inline-flex items-center gap-3 mt-10 bg-brand-cream text-brand-forest px-10 py-4 rounded-full text-sm tracking-wide hover:-translate-y-1 transition-transform duration-300"
            >
              Return home <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </FadeUp>
        )}

        {(state === "failed" || state === "timeout") && (
          <FadeUp>
            <div className="w-20 h-20 rounded-full border border-brand-cream/40 mx-auto flex items-center justify-center">
              <X size={40} className="text-brand-cream/70" />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl mt-8">
              {state === "timeout" ? "Still processing…" : "Payment not completed"}
            </h1>
            <p className="text-brand-cream/70 mt-4">
              {state === "timeout"
                ? "This is taking longer than usual. Please check your email for confirmation."
                : "It looks like the payment didn't go through. You're welcome to try again."}
            </p>
            <Link
              to="/packages"
              data-testid="success-retry-link"
              className="inline-flex items-center gap-3 mt-10 border border-brand-cream/40 px-10 py-4 rounded-full text-sm tracking-wide hover:bg-brand-cream/10 transition-colors duration-300"
            >
              Back to packages
            </Link>
          </FadeUp>
        )}
      </div>
    </div>
  );
}
